import React, { Suspense, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';

const MODEL_PATH = '/models/source/stia/stia_all_parts.glb';


// Preload the single GLB
useGLTF.preload(MODEL_PATH);

const BOAT_CENTER = [0, 0, 0];

// ── Camera system ──────────────────────────────────────────────
const MANUAL_VIEWS = {
  exterior: { position: [10, 3, 8], target: BOAT_CENTER },
  front:    { position: [3, 2, 10], target: BOAT_CENTER },
  top:      { position: [0, 12, 0.1], target: BOAT_CENTER },
  stern:    { position: [-6, 3, -6], target: BOAT_CENTER },
};

const CATEGORY_CAMERAS = {
  tops:    { position: [10, 3, 8], target: BOAT_CENTER },
  engines: { position: [-7, 3, -5], target: BOAT_CENTER },
  seating: { position: [6, 3, 6], target: BOAT_CENTER },
  extras:  { position: [-8, 4, -6], target: BOAT_CENTER },
};

const OPTION_CAMERAS = {
  bimini_open:   { offset: [4, 5, 4], lookShift: [0, -0.5, 0] },
  bimini_folded: { offset: [4, 4, 4], lookShift: [0, -0.3, 0] },
  outboard:      { offset: [-4, 3, -4], lookShift: [0, 0, 0] },
  fore_seat:     { offset: [4, 2.5, 5], lookShift: [0, 0, 0] },
  fore_sunbed:   { offset: [4, 2.5, 5], lookShift: [0, 0, 0] },
  aft_sunbed:    { offset: [-4, 2.5, -4], lookShift: [0, 0, 0] },
  foreCushion:   { offset: [4, 3, 5], lookShift: [0, 0, 0] },
  foreTableUp:   { offset: [4, 3, 4], lookShift: [0, 0, 0] },
  foreTableDown: { offset: [4, 3, 4], lookShift: [0, 0, 0] },
  gps:           { offset: [-3, 2.5, -4], lookShift: [0, 0.2, 0] },
  windshield:    { offset: [-3, 2.5, -5], lookShift: [0, 0, 0] },
  multimedia:    { offset: [-3, 2.5, -4], lookShift: [0, 0.2, 0] },
  sideRails:     { offset: [6, 2, 0], lookShift: [0, 0, 0] },
};

// Transform a local model-space point through the boat group transform
function localToWorld(lx, ly, lz) {
  const s = 1.2;
  const sx = lx * s, sy = ly * s, sz = lz * s;
  const cos = Math.cos(Math.PI / 4);
  const sin = Math.sin(Math.PI / 4);
  return [sx * cos + sz * sin, sy - 1.15, -sx * sin + sz * cos];
}

// Compute world-space bbox center of a named group within the scene
function computeGroupCenter(scene, groupName) {
  let target = null;
  scene.traverse((child) => {
    if (child.name === groupName) target = child;
  });
  if (!target) return null;
  const box = new THREE.Box3().setFromObject(target);
  const c = new THREE.Vector3();
  box.getCenter(c);
  return localToWorld(c.x, c.y, c.z);
}

// Map option camera keys to GLB group names for center computation
const OPTION_TO_GROUP = {
  bimini_open: 'bimini',
  bimini_folded: 'bimini_folded',
  outboard: 'motor',
  fore_seat: 'front_seat_backrest',
  fore_sunbed: 'side_cushions',
  aft_sunbed: 'aft_sunbed_backrest',
  foreCushion: 'front_cushion',
  foreTableUp: 'table_open',
  foreTableDown: 'table_large_sun_bed',
  gps: 'GPS',
  windshield: 'console_windshield',
  multimedia: 'multimedia',
  sideRails: 'rails',
};

function ModelCenterComputer({ centersRef, scene }) {
  useEffect(() => {
    if (!scene) return;
    const centers = {};
    for (const [optionKey, groupName] of Object.entries(OPTION_TO_GROUP)) {
      const center = computeGroupCenter(scene, groupName);
      if (center) centers[optionKey] = center;
    }
    centersRef.current = centers;
  }, [scene, centersRef]);

  return null;
}

// Map GLB group names → selection logic
// Groups inside "for_web" that are toggled on/off
const GROUP_TO_SELECTION = {
  // Tops
  bimini:          (sel) => sel.top === 'bimini_open',
  bimini_folded:   (sel) => sel.top === 'bimini_folded',
  // Engine
  bracket:         (sel) => sel.engine === 'outboard',
  // Seating (now supports multiple selections)
  front_seat_backrest:    (sel) => sel.seating?.fore_seat,
  front_seat:             (sel) => sel.seating?.fore_seat,
  aft_sunbed_backrest:    (sel) => sel.seating?.aft_sunbed,
  aft_sunbed:             (sel) => sel.seating?.aft_sunbed,
  aft_seat:               (sel) => sel.seating?.aft_sunbed,
  aft_seat_default:       (sel) => !sel.seating?.aft_sunbed,  // Show default only if aft_sunbed NOT selected
  side_cushions:          (sel) => sel.seating?.fore_sunbed,
  // Extras
  front_cushion:          (sel) => sel.extras?.foreCushion,
  table_open:             (sel) => sel.extras?.foreTableUp,
  table_large_sun_bed:    (sel) => sel.extras?.foreTableDown,
  GPS:                    (sel) => sel.extras?.gps,
  console_windshield:     (sel) => sel.extras?.windshield,
  multimedia:             (sel) => sel.extras?.multimedia,
  rails:                  (sel) => sel.extras?.sideRails,
};


function enhanceMaterials(scene) {
  scene.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const mat = child.material;
    if (mat._enhanced) return;
    mat._enhanced = true;

    const matName = mat.name || '';

    // Handle glass/transparent materials differently
    if (matName === 'polyglass') {
      mat.side = THREE.FrontSide;
      mat.roughness = 0;
      mat.metalness = 0;
    } else {
      mat.side = THREE.DoubleSide;
    }
    mat.needsUpdate = true;

    // Make boat bottom white
    if (child.name && (child.name.toLowerCase().includes('bottom') ||
                       child.name.includes('floorBOTTOM'))) {
      mat.color.setHex(0xFFFFFF);
    }
  });
}

function optimizeScene(scene) {
  if (scene._optimized) return;
  scene._optimized = true;

  scene.traverse((child) => {
    if (!child.isMesh) return;
    child.renderOrder = 0;
    child.frustumCulled = true;
    if (child.geometry) {
      child.geometry.computeBoundingSphere();
    }
    child.castShadow = false;
    child.receiveShadow = false;
  });
}

// cameraIntent: { type: 'category'|'option'|'manual', key: string, version: number }
function CameraAnimator({ cameraIntent, controlsRef, modelCentersRef }) {
  const { camera } = useThree();
  const targetPos = useRef(new THREE.Vector3(10, 3, 8));
  const targetLook = useRef(new THREE.Vector3(...BOAT_CENTER));
  const animating = useRef(false);
  const animProgress = useRef(0);
  const startPos = useRef(new THREE.Vector3());
  const startLook = useRef(new THREE.Vector3());
  const lastVersion = useRef(-1);

  useEffect(() => {
    if (!cameraIntent || cameraIntent.version === lastVersion.current) return;
    lastVersion.current = cameraIntent.version;

    const { type, key } = cameraIntent;
    let newPos, newLook;

    if (type === 'option') {
      return;
    }

    if (type === 'category' && CATEGORY_CAMERAS[key]) {
      const cam = CATEGORY_CAMERAS[key];
      newPos = cam.position;
      // Keep the current orbit target so orbit doesn't change
      if (controlsRef.current) {
        const t = controlsRef.current.target;
        newLook = [t.x, t.y, t.z];
      } else {
        newLook = cam.target;
      }
    }

    if (!newPos && type === 'manual' && MANUAL_VIEWS[key]) {
      const cam = MANUAL_VIEWS[key];
      newPos = cam.position;
      newLook = cam.target;
    }

    if (!newPos) return;

    targetPos.current.set(...newPos);
    targetLook.current.set(...newLook);

    if (camera.position.distanceTo(targetPos.current) < 0.1) {
      if (controlsRef.current) {
        controlsRef.current.target.set(...newLook);
        controlsRef.current.update();
      }
      return;
    }

    startPos.current.copy(camera.position);
    startLook.current.copy(
      controlsRef.current ? controlsRef.current.target : new THREE.Vector3(...BOAT_CENTER)
    );
    animProgress.current = 0;
    animating.current = true;
    if (controlsRef.current) controlsRef.current.enabled = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraIntent]);

  useFrame((_, delta) => {
    if (!animating.current) return;

    const totalDist = startPos.current.distanceTo(targetPos.current);
    const duration = Math.max(0.8, Math.min(1.8, totalDist * 0.15));

    animProgress.current = Math.min(animProgress.current + delta / duration, 1);

    const p = animProgress.current;
    const ease = p < 0.5
      ? 4 * p * p * p
      : 1 - Math.pow(-2 * p + 2, 3) / 2;

    camera.position.lerpVectors(startPos.current, targetPos.current, ease);
    if (controlsRef.current) {
      controlsRef.current.target.lerpVectors(startLook.current, targetLook.current, ease);
      controlsRef.current.update();
    }

    if (animProgress.current >= 1) {
      camera.position.copy(targetPos.current);
      if (controlsRef.current) {
        controlsRef.current.target.copy(targetLook.current);
        controlsRef.current.enabled = true;
        controlsRef.current.update();
      }
      animating.current = false;
    }
  });

  return null;
}


const BoatModel = React.memo(({ selections, controlsRef }) => {
  const { scene } = useGLTF(MODEL_PATH);
  const groupRef = useRef();
  const innerRef = useRef();
  const centeredRef = useRef(false);
  const boatCenterRef = useRef(new THREE.Vector3());

  // Enhance materials once on load and hide flooring by default
  useEffect(() => {
    enhanceMaterials(scene);
    optimizeScene(scene);

    // Make boat hull/bottom and bimini top white, upholstery blue
    scene.traverse((child) => {
      if (child.isMesh && child.material) {
        const matName = child.material.name || 'unnamed';
        const name = child.name || '';

        console.log(`Mesh: ${name}, Material: ${matName}, Color: ${child.material.color?.getHexString() || 'N/A'}`);

        // Set hull and bimini hood to white
        if (matName === 'hull' || matName === 'bimini_hood') {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.color.setHex(0xFFFFFF);
              mat.needsUpdate = true;
            });
          } else {
            child.material.color.setHex(0xFFFFFF);
            child.material.needsUpdate = true;
          }
          console.log(`✓ Set ${name} (${matName}) to WHITE`);
        }

        // Set upholstery to tan/beige
        if (matName === 'upholstery') {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.color.setHex(0xC8A085);
              mat.needsUpdate = true;
            });
          } else {
            child.material.color.setHex(0xC8A085);
            child.material.needsUpdate = true;
          }
          console.log(`✓ Set ${name} (${matName}) to TAN`);
        }
      }
    });
  }, [scene]);

  // Show/hide groups based on selections
  useEffect(() => {
    scene.traverse((child) => {
      // Check for flooring meshes by material name and hide them by default
      if (child.isMesh && child.material) {
        const matName = child.material.name || '';
        if (matName === 'floorTOP' || matName === 'floorBOTTOM') {
          child.visible = selections.extras?.evaDecking === true;
          return;
        }
      }

      const name = child.name;
      if (!name) return;

      // Check if this group has a selection rule
      if (GROUP_TO_SELECTION[name] !== undefined) {
        child.visible = GROUP_TO_SELECTION[name](selections);
        // Log only groups without rules when aft_sunbed is selected
        if (selections.seating?.aft_sunbed && (name.toLowerCase().includes('aft') || name.toLowerCase().includes('seat'))) {
          console.log(`GROUP WITH RULE: ${name}, visible=${child.visible}`);
        }
        return;
      }

      // Log all aft/seat groups
      const lowerName = name.toLowerCase();
      if (lowerName.includes('aft') || lowerName.includes('seat')) {
        const hasRule = GROUP_TO_SELECTION[name] !== undefined;
        console.log(`AFT/SEAT: ${name}, hasRule=${hasRule}, visible=${child.visible}, rule_result=${hasRule ? GROUP_TO_SELECTION[name](selections) : 'N/A'}`);
      }

      // Motor (top-level group) — tied to engine selection
      if (name === 'motor') {
        child.visible = selections.engine === 'outboard';
        return;
      }
    });
  }, [selections, scene]);

  // Compute orbit center on the first frame where both model and controls are ready.
  useFrame(() => {
    if (centeredRef.current || !innerRef.current || !controlsRef?.current) return;
    innerRef.current.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(innerRef.current);
    const center = new THREE.Vector3();
    box.getCenter(center);
    boatCenterRef.current = center.clone();
    controlsRef.current.target.copy(center);
    controlsRef.current.update();
    centeredRef.current = true;
  });

  // Boat rocking
  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.getElapsedTime();

    const pitch = Math.sin(t * 0.5) * 0.006
                + Math.sin(t * 0.9 + 0.5) * 0.003;
    const roll = Math.sin(t * 0.4 + 0.8) * 0.008
               + Math.sin(t * 0.7 + 2.0) * 0.004;
    const heave = Math.sin(t * 0.45) * 0.012
                + Math.sin(t * 0.8 + 1.5) * 0.006;

    groupRef.current.rotation.x = pitch;
    groupRef.current.rotation.z = roll;
    groupRef.current.position.y = heave;
  });

  return (
    <group ref={groupRef}>
      <group ref={innerRef} scale={1.2} position={[0, -1.15, 0]} rotation={[0, Math.PI / 4, 0]}>
        <primitive object={scene} />
      </group>
    </group>
  );
});

function LoadingOverlay({ visible }) {
  return (
    <div className={`viewer-loading ${visible ? 'viewer-loading--visible' : ''}`}>
      <div className="viewer-loading__spinner" />
      <p className="viewer-loading__text">Loading 3D Model...</p>
    </div>
  );
}

function AutoRotate({ controlsRef }) {
  const idleTimer = useRef(null);
  const isIdle = useRef(false);
  const hasInteracted = useRef(false);

  useEffect(() => {
    const onInteract = () => {
      hasInteracted.current = true;
      isIdle.current = false;
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
      }, 4000);
    };

    window.addEventListener('pointermove', onInteract);
    window.addEventListener('pointerdown', onInteract);
    return () => {
      clearTimeout(idleTimer.current);
      window.removeEventListener('pointermove', onInteract);
      window.removeEventListener('pointerdown', onInteract);
    };
  }, []);

  useFrame(() => {
    if (!controlsRef.current) return;
    controlsRef.current.autoRotate = isIdle.current;
    controlsRef.current.autoRotateSpeed = 0.6;
  });

  return null;
}

function SceneContent({ selections, cameraIntent, controlsRef, onLoaded }) {
  const { scene } = useGLTF(MODEL_PATH);
  const modelCentersRef = useRef({});

  useEffect(() => {
    const timer = setTimeout(() => onLoaded(), 300);
    return () => clearTimeout(timer);
  }, [onLoaded]);

  return (
    <>
      <ambientLight intensity={0.3} />
      <directionalLight position={[5, 10, 6]} intensity={0.6} color="#ffffff" />
      <directionalLight position={[-4, 8, -4]} intensity={0.2} color="#ffffff" />
      <spotLight position={[0, 14, 0]} intensity={0.4} angle={0.6} penumbra={1} color="#ffffff" />

      <Environment preset="studio" background={false} environmentIntensity={0.4} />

      <color attach="background" args={['#1a2a3a']} />

      <ModelCenterComputer centersRef={modelCentersRef} scene={scene} />
      <BoatModel selections={selections} controlsRef={controlsRef} />

      <CameraAnimator cameraIntent={cameraIntent} controlsRef={controlsRef} modelCentersRef={modelCentersRef} />
      <AutoRotate controlsRef={controlsRef} />

      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        minDistance={5}
        maxDistance={18}
        minPolarAngle={0.2}
        maxPolarAngle={Math.PI / 2.1}
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.6}
        zoomSpeed={0.8}
      />
    </>
  );
}

function BoatViewer({ selections, cameraIntent, onScreenshot }) {
  const controlsRef = useRef();
  const canvasRef = useRef();
  const [loading, setLoading] = useState(true);
  // Always start at the same position as MANUAL_VIEWS.exterior so CameraAnimator
  // never triggers an animation on mount. Use a wider FOV on mobile to fill the screen.
  const cameraConfig = useMemo(() => ({
    position: [10, 3, 8],
    fov: window.innerWidth <= 767 ? 52 : 35,
    near: 0.1,
    far: 1000,
  }), []);

  const handleLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  // Force-prevent page scroll on touch so OrbitControls gets every touch event.
  // CSS touch-action:none is unreliable on iOS Chrome inside flex containers.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const prevent = (e) => { if (e.cancelable) e.preventDefault(); };
    el.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      el.removeEventListener('touchmove', prevent);
    };
  }, []);

  // Auto-capture screenshot after scene loads (used by FinishBuild for PDF)
  useEffect(() => {
    if (!loading && onScreenshot) {
      const timer = setTimeout(() => {
        const canvas = canvasRef.current?.querySelector('canvas');
        if (canvas) onScreenshot(canvas.toDataURL('image/png'));
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [loading, onScreenshot]);

  const handleScreenshot = useCallback(() => {
    if (!canvasRef.current || !onScreenshot) return;
    const canvas = canvasRef.current.querySelector('canvas');
    if (canvas) {
      onScreenshot(canvas.toDataURL('image/png'));
    }
  }, [onScreenshot]);

  const glConfig = useMemo(() => ({
    antialias: true,
    preserveDrawingBuffer: !!onScreenshot,
    powerPreference: 'high-performance',
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 0.5,
  }), [onScreenshot]);

  return (
    <div className="boat-viewer" ref={canvasRef}>
      <LoadingOverlay visible={loading} />
      <Canvas
        camera={cameraConfig}
        dpr={[1, 2]}
        performance={{ min: 0.5 }}
        gl={glConfig}
        style={{ width: '100%', height: '100%', touchAction: 'none' }}
      >
        <Suspense fallback={null}>
          <SceneContent
            selections={selections}
            cameraIntent={cameraIntent}
            controlsRef={controlsRef}
            onLoaded={handleLoaded}
          />
        </Suspense>
      </Canvas>

      {onScreenshot && (
        <button className="viewer-screenshot-btn" onClick={handleScreenshot} title="Take screenshot">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="2" y="4" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="9" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 4L7 2H11L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}

export default BoatViewer;
